import { Button, Input, Rate, Space, Table, Tag } from "antd";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import ViewBtn from "../Buttons/ViewBtn";
import BtnViewSvg from "../UI/Button/btnViewSvg";

function GetAllPerformanceFeedback({ data = [], onEdit, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const [searchEmployee, setSearchEmployee] = useState("");
	const [searchReviewer, setSearchReviewer] = useState("");
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const getRatingColor = (rating) => {
		if (rating >= 4) return "green";
		if (rating >= 3) return "blue";
		if (rating >= 2) return "orange";
		return "red";
	};

	const columns = [
		{
			id: 1,
			title: "S.No",
			dataIndex: "serial",
			key: "serial",
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Reviewer",
			dataIndex: "reviewerName",
			key: "reviewerName",
		},
		{
			title: "Feedback Date",
			dataIndex: "feedbackDate",
			key: "feedbackDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Review Period",
			dataIndex: "reviewPeriod",
			key: "reviewPeriod",
		},
		{
			title: "Overall Rating",
			dataIndex: "overallRating",
			key: "overallRating",
			render: (rating) => (
				<Space>
					<Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} />
					<Tag color={getRatingColor(rating)}>{rating}/5</Tag>
				</Space>
			),
		},
		{
			title: "Technical Skills",
			dataIndex: "technicalSkills",
			key: "technicalSkills",
			render: (rating) => (
				<Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} />
			),
		},
		{
			title: "Communication",
			dataIndex: "communication",
			key: "communication",
			render: (rating) => (
				<Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} />
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => {
				if (status === "Completed") {
					return <Tag color="green">Completed</Tag>;
				} else if (status === "Draft") {
					return <Tag color="orange">Draft</Tag>;
				} else {
					return <Tag color="blue">Pending</Tag>;
				}
			},
		},
		{
			title: "Actions",
			key: "action",
			render: (_, record) => {
				const id = record.id;
				return (
					<Space>
						<ViewBtn
							path={`/admin/performanceFeedback/${id}`}
							text="View"
							icon={<BtnViewSvg />}
						/>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => onEdit && onEdit(record)}
						/>
					</Space>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || index,
			serial: index + 1,
			employeeName: i.employeeName || mapEmployeeName(i.employeeId),
			reviewerName: i.reviewerName || mapEmployeeName(i.reviewerId),
		}));

	const filteredData = addKeys(data).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		const reviewerName = (item.reviewerName || "").toLowerCase();
		const matchesEmployee = employeeName.includes(searchEmployee.toLowerCase());
		const matchesReviewer = reviewerName.includes(searchReviewer.toLowerCase());
		return matchesEmployee && matchesReviewer;
	});

	return (
		<div className="performance-feedback-table-container">
			<div className="text-center my-2 flex justify-between items-center">
				<h5 className="text-color-2 text-xl mb-2">Performance Feedback List</h5>
				{data && data.length > 0 && (
					<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
						<ColVisibilityDropdown
							options={columns}
							columns={columns}
							columnsToShowHandler={columnsToShowHandler}
						/>
						<Input
							placeholder="Search by Employee"
							value={searchEmployee}
							onChange={(e) => setSearchEmployee(e.target.value)}
							style={{ width: 200 }}
						/>
						<Input
							placeholder="Search by Reviewer"
							value={searchReviewer}
							onChange={(e) => setSearchReviewer(e.target.value)}
							style={{ width: 200 }}
						/>
					</div>
				)}
			</div>

			<Table
				scroll={{ x: true }}
				loading={false}
				columns={columnsToShow}
				dataSource={filteredData}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
					pageSizeOptions: ["10", "20", "50", "100"],
				}}
			/>
		</div>
	);
}

export default GetAllPerformanceFeedback;

